import type { IToast, IToastMessage } from '../../toast/interface';
import type { IModuleTranslate } from './translate';
import { TranslationKey } from './translate';

export enum Message {
    DownloadCompleted = 1,
    DownloadFailed_Offline = 2,
    DownloadFailed_UnexpectedError = 3,
    DownloadFailed_NotEnoughSpace = 4,
}

export interface IModuleToast {
    isShowing: (message: Message) => boolean;
    isAnyShowing: () => boolean;
    show: (message: Message, parameters?: string[]) => void;
    hide: (message: Message) => void;
    hideAll: () => void;
}

const MessageTranslationKey: Record<Message, TranslationKey> = {
    [Message.DownloadCompleted]: TranslationKey.DOWNLOAD_COMPLETE,
    [Message.DownloadFailed_Offline]: TranslationKey.DOWNLOAD_FAILED_OFFLINE,
    [Message.DownloadFailed_UnexpectedError]: TranslationKey.DOWNLOAD_FAILED,
    [Message.DownloadFailed_NotEnoughSpace]: TranslationKey.DOWNLOAD_FAILED_NOT_ENOUGH_SPACE,
};

const KEY_PREFIX = 'DownloadTour-';

export const DEFAULT_MESSAGE: Record<Message, Omit<Required<IToastMessage>, 'text'>> = {
    [Message.DownloadCompleted]: {
        id: `${KEY_PREFIX}-Completed`,
        type: 'success',
    },
    [Message.DownloadFailed_Offline]: {
        id: `${KEY_PREFIX}-Err-Offline`,
        type: 'warn',
    },
    [Message.DownloadFailed_UnexpectedError]: {
        id: `${KEY_PREFIX}-Err-Unknown`,
        type: 'error',
    },
    [Message.DownloadFailed_NotEnoughSpace]: {
        id: `${KEY_PREFIX}-Err-Space`,
        type: 'error',
    },
};

const _isShowingToast = (toast: IToast, id: string) => toast.isShowing({ id });
const _showToast = (toast: IToast, message: Required<IToastMessage>) => {
    !_isShowingToast(toast, message.id) && toast.show(message);
};
const _hideToast = (toast: IToast, id: string) => {
    toast.hide({ id });
};

function ModuleToast(translater: IModuleTranslate, toast: IToast): IModuleToast {
    if (!translater) {
        throw new Error(`Download-Tour: Missing translater object`);
    }

    if (!toast) {
        throw new Error(`Download-Tour: Missing toast object`);
    }

    return {
        isShowing: (message: Message) => _isShowingToast(toast, DEFAULT_MESSAGE[message].id),
        isAnyShowing: () => {
            const messageShow = Object.values(Message).map(message => {
                const messageDetails = DEFAULT_MESSAGE[message as Message];
                return _isShowingToast(toast, messageDetails.id ?? '');
            });
            const showCount = messageShow.reduce((count, value) => count + (value ? 1 : 0), 0);
            return showCount > 0;
        },
        show: (message, parameters) => {
            const text = translater.translate(MessageTranslationKey[message], parameters);

            _showToast(toast, {
                ...DEFAULT_MESSAGE[message],
                text,
            });
        },
        hide: (type: Message) => {
            _hideToast(toast, DEFAULT_MESSAGE[type].id);
        },
        hideAll: () => {
            for (const message of Object.values(DEFAULT_MESSAGE)) {
                _hideToast(toast, message.id);
            }
        },
    };
}

export default ModuleToast;
